import React, { useMemo } from 'react';
import TemplateTextField from './TemplateTextField';

const StepFees = ({ formData, updateFormData, nextStep, prevStep, selectedPortfolio, disabled }) => {
  const data = formData.fees;

  const handleChange = (field, value) => {
    updateFormData('fees', { [field]: value });
  };

  const fees = useMemo(() => {
    const adviserFee = parseFloat(data.adviserFee) || 0;
    const platformFee = parseFloat(data.platformFee) || 0;
    const portfolioIcr = parseFloat(data.portfolioIcr) || 0;
    const totalOngoing = adviserFee + platformFee + portfolioIcr;
    const investmentAmount = parseFloat(data.investmentAmount) || 0;

    return {
      adviserFee,
      platformFee,
      portfolioIcr,
      totalOngoing,
      investmentAmount,
      adviserDollar: investmentAmount * adviserFee / 100,
      platformDollar: investmentAmount * platformFee / 100,
      icrDollar: investmentAmount * portfolioIcr / 100,
      totalDollar: investmentAmount * totalOngoing / 100,
    };
  }, [data]);

  return (
    <fieldset disabled={disabled}>
      <h4 className="soa-step-title">Fees &amp; Costs</h4>
      <p className="soa-step-subtitle">Detail all fees associated with implementing and maintaining this advice</p>

      {/* One-off fees */}
      <div className="soa-section-banner">
        <i className="bi bi-receipt"></i>
        One-Off Fees
      </div>

      <div className="row">
        <div className="col-md-6 soa-form-group">
          <label>Implementation / Advice Preparation Fee ($)</label>
          <input
            type="number"
            className="form-control"
            value={data.implementationFee}
            onChange={(e) => handleChange('implementationFee', e.target.value)}
            placeholder="e.g. 3300"
          />
        </div>
      </div>

      {/* Ongoing fees */}
      <div className="soa-section-banner">
        <i className="bi bi-arrow-repeat"></i>
        Ongoing Fees (% per annum)
      </div>

      <div className="row">
        <div className="col-md-4 soa-form-group">
          <label>Adviser Service Fee (%)</label>
          <input
            type="number"
            step="0.01"
            className="form-control"
            value={data.adviserFee}
            onChange={(e) => handleChange('adviserFee', e.target.value)}
            placeholder="e.g. 0.88"
          />
        </div>
        <div className="col-md-4 soa-form-group">
          <label>Platform / Admin Fee (%)</label>
          <input
            type="number"
            step="0.01"
            className="form-control"
            value={data.platformFee}
            onChange={(e) => handleChange('platformFee', e.target.value)}
            placeholder="e.g. 0.30"
          />
        </div>
        <div className="col-md-4 soa-form-group">
          <label>
            Portfolio ICR (%)
            {selectedPortfolio && <span className="text-muted ms-1" style={{ fontWeight: 400 }}>(auto-filled)</span>}
          </label>
          <input
            type="number"
            step="0.01"
            className="form-control"
            value={data.portfolioIcr}
            onChange={(e) => handleChange('portfolioIcr', e.target.value)}
            placeholder="e.g. 0.45"
          />
        </div>
      </div>

      {/* Investment amount for dollar calculation */}
      <div className="row">
        <div className="col-md-6 soa-form-group">
          <label>Investment Amount ($) — for fee calculations</label>
          <input
            type="number"
            className="form-control"
            value={data.investmentAmount}
            onChange={(e) => handleChange('investmentAmount', e.target.value)}
            placeholder="e.g. 500000"
          />
        </div>
      </div>

      {/* Fee summary */}
      <div className="soa-section-banner">
        <i className="bi bi-calculator"></i>
        Fee Summary
      </div>

      <table className="table table-sm" style={{ fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ backgroundColor: 'var(--primary-color)', color: '#fff' }}>
            <th>Fee Component</th>
            <th className="text-end">Rate (% p.a.)</th>
            {fees.investmentAmount > 0 && <th className="text-end">Amount ($ p.a.)</th>}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Adviser Service Fee</td>
            <td className="text-end">{fees.adviserFee.toFixed(2)}%</td>
            {fees.investmentAmount > 0 && <td className="text-end">${fees.adviserDollar.toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>}
          </tr>
          <tr>
            <td>Platform / Admin Fee</td>
            <td className="text-end">{fees.platformFee.toFixed(2)}%</td>
            {fees.investmentAmount > 0 && <td className="text-end">${fees.platformDollar.toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>}
          </tr>
          <tr>
            <td>Investment Cost Ratio (ICR)</td>
            <td className="text-end">{fees.portfolioIcr.toFixed(2)}%</td>
            {fees.investmentAmount > 0 && <td className="text-end">${fees.icrDollar.toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>}
          </tr>
        </tbody>
      </table>

      <div className="soa-fee-total">
        <span>Total Ongoing Cost</span>
        <span>
          {fees.totalOngoing.toFixed(2)}%
          {fees.investmentAmount > 0 && ` — $${fees.totalDollar.toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} p.a.`}
        </span>
      </div>

      {/* Current Fees Comparison */}
      <div className="soa-section-banner">
        <i className="bi bi-arrow-left-right"></i>
        Current Fees (for comparison)
      </div>

      <div className="row">
        <div className="col-md-4 soa-form-group">
          <label>Current Adviser Fee (%)</label>
          <input
            type="number"
            step="0.01"
            className="form-control"
            value={data.currentFees?.adviserFee || ''}
            onChange={(e) => handleChange('currentFees', { ...data.currentFees, adviserFee: e.target.value })}
            placeholder="e.g. 1.10"
          />
        </div>
        <div className="col-md-4 soa-form-group">
          <label>Current Platform Fee (%)</label>
          <input
            type="number"
            step="0.01"
            className="form-control"
            value={data.currentFees?.platformFee || ''}
            onChange={(e) => handleChange('currentFees', { ...data.currentFees, platformFee: e.target.value })}
            placeholder="e.g. 0.50"
          />
        </div>
        <div className="col-md-4 soa-form-group">
          <label>Current ICR (%)</label>
          <input
            type="number"
            step="0.01"
            className="form-control"
            value={data.currentFees?.icr || ''}
            onChange={(e) => handleChange('currentFees', { ...data.currentFees, icr: e.target.value })}
            placeholder="e.g. 0.65"
          />
        </div>
      </div>

      {/* Fee comparison display */}
      {(data.currentFees?.adviserFee || data.currentFees?.platformFee || data.currentFees?.icr) && (
        <div className="mb-3">
          {(() => {
            const curTotal = (parseFloat(data.currentFees?.adviserFee) || 0) +
                            (parseFloat(data.currentFees?.platformFee) || 0) +
                            (parseFloat(data.currentFees?.icr) || 0);
            const diff = fees.totalOngoing - curTotal;
            const isSaving = diff < 0;
            return (
              <div className="p-2 rounded small" style={{
                backgroundColor: isSaving ? '#e8f5e9' : '#fff3e0',
                border: `1px solid ${isSaving ? 'var(--success-color)' : 'var(--secondary-color)'}`,
              }}>
                <strong>Current total: {curTotal.toFixed(2)}%</strong> → <strong>Proposed total: {fees.totalOngoing.toFixed(2)}%</strong>
                {' '}
                <span style={{ color: isSaving ? '#2e7d32' : '#e65100', fontWeight: 600 }}>
                  ({isSaving ? 'Saving' : 'Increase'}: {Math.abs(diff).toFixed(2)}%
                  {fees.investmentAmount > 0 && ` = $${Math.abs(fees.investmentAmount * diff / 100).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} p.a.`})
                </span>
              </div>
            );
          })()}
        </div>
      )}

      <TemplateTextField
        fieldKey="fees.feeBenefitStatement"
        label="Fee Benefit Statement"
        value={data.feeBenefitStatement || ''}
        onChange={(val) => handleChange('feeBenefitStatement', val)}
        placeholder="Explain how the proposed fee structure benefits the client compared to their current arrangement..."
        rows={3}
        disabled={disabled}
      />

      <div className="soa-form-group">
        <label>Additional Fee Notes</label>
        <textarea
          className="form-control"
          value={data.feeNotes}
          onChange={(e) => handleChange('feeNotes', e.target.value)}
          placeholder="Any additional information about fees, rebates, or fee arrangements..."
          rows={3}
        />
      </div>

      <div className="soa-step-nav">
        <button className="btn btn-outline-secondary" onClick={prevStep}>
          <i className="bi bi-arrow-left me-1"></i>Back
        </button>
        <button className="btn btn-primary" onClick={nextStep}>
          Next: Actions & Authority <i className="bi bi-arrow-right ms-1"></i>
        </button>
      </div>
    </fieldset>
  );
};

export default StepFees;
